import { useMemo, useRef } from 'react';
import * as THREE from 'three';
import { useFrame } from '@react-three/fiber';
import { lerp, easeInOut } from '../../lab/shared/PouringCylinder';
import { Thermometer } from '../../lab/shared/Thermometer';
import { BoilingTube } from '../../lab/shared/BoilingTube';
import ConicalFlask from '../../lab/shared/ConicalFlask';

const SPATULA_REST: [number, number, number] = [0.9, 0.5, 0.05];
const SPATULA_POUR: [number, number, number] = [0.05, 1.95, 0];
const SPATULA_REST_ROT: [number, number, number] = [0, 0, 0];
const SPATULA_POUR_ROT: [number, number, number] = [0, 0, -0.6];
const SPATULA_TIP_TILT: [number, number, number] = [0, 0, -1.4];

const TUBE_TOP_Y = 1.825;
const TUBE_FINAL_Y = 1.4;
const NAPH_FALL_PARTICLES = 30;

interface SpatulaNaphthaleneProps {
  active: boolean;
  progress: number;
}

export function SpatulaNaphthalenePour({ active, progress }: SpatulaNaphthaleneProps) {
  const fallRef = useRef<THREE.InstancedMesh>(null);
  const dummy = useMemo(() => new THREE.Object3D(), []);

  const seeds = useMemo(() => Array.from({ length: NAPH_FALL_PARTICLES }, () => ({
    speed: 0.45 + Math.random() * 0.4,
    phase: Math.random(),
    xJitter: (Math.random() - 0.5) * 0.025,
    zJitter: (Math.random() - 0.5) * 0.025,
    size: 0.005 + Math.random() * 0.004,
    spin: Math.random() * Math.PI * 2,
  })), []);

  let moveT = 0;
  if (progress < 0.3) moveT = progress / 0.3;
  else if (progress < 0.85) moveT = 1;
  else moveT = (1 - progress) / 0.15;
  moveT = easeInOut(Math.max(0, Math.min(1, moveT)));

  let tiltT = 0;
  if (progress >= 0.3 && progress < 0.4) tiltT = (progress - 0.3) / 0.1;
  else if (progress >= 0.4 && progress < 0.78) tiltT = 1;
  else if (progress >= 0.78 && progress < 0.88) tiltT = 1 - (progress - 0.78) / 0.1;
  tiltT = easeInOut(Math.max(0, Math.min(1, tiltT)));

  let saltAmount = 1;
  if (progress >= 0.35 && progress < 0.78) saltAmount = 1 - (progress - 0.35) / 0.43;
  else if (progress >= 0.78) saltAmount = 0;
  saltAmount = Math.max(0, saltAmount);

  const px = lerp(SPATULA_REST[0], SPATULA_POUR[0], moveT);
  const py = lerp(SPATULA_REST[1], SPATULA_POUR[1], moveT);
  const pz = lerp(SPATULA_REST[2], SPATULA_POUR[2], moveT);
  const rotZ = lerp(SPATULA_REST_ROT[2], SPATULA_TIP_TILT[2], tiltT);

  const tipLocalX = -0.085;
  const lipX = px + tipLocalX * Math.cos(rotZ);
  const lipY = py + tipLocalX * Math.sin(rotZ);
  const lipZ = pz;
  const targetX = SPATULA_POUR[0];
  const targetY = TUBE_TOP_Y - 0.05;
  const targetZ = 0;

  useFrame((state) => {
    if (!fallRef.current) return;
    const showStream = active && progress > 0.36 && progress < 0.82;

    if (!showStream) {
      for (let i = 0; i < NAPH_FALL_PARTICLES; i++) {
        dummy.position.set(0, -999, 0);
        dummy.scale.setScalar(0);
        dummy.updateMatrix();
        fallRef.current.setMatrixAt(i, dummy.matrix);
      }
      fallRef.current.instanceMatrix.needsUpdate = true;
      return;
    }

    const t = state.clock.elapsedTime;
    const intensity = 0.4 + 0.6 * saltAmount;
    const visibleCount = Math.round(NAPH_FALL_PARTICLES * intensity);

    for (let i = 0; i < NAPH_FALL_PARTICLES; i++) {
      const s = seeds[i];
      if (i >= visibleCount) {
        dummy.position.set(0, -999, 0);
        dummy.scale.setScalar(0);
      } else {
        const cycle = (t * s.speed + s.phase) % 1;
        const x = lerp(lipX, targetX, cycle) + s.xJitter;
        const y = lerp(lipY, targetY, cycle) - Math.sin(cycle * Math.PI) * 0.01;
        const z = lerp(lipZ, targetZ, cycle) + s.zJitter;
        dummy.position.set(x, y, z);
        dummy.scale.setScalar(s.size);
        dummy.rotation.set(t * 1.4 + s.spin, t * 1.0, 0);
      }
      dummy.updateMatrix();
      fallRef.current.setMatrixAt(i, dummy.matrix);
    }
    fallRef.current.instanceMatrix.needsUpdate = true;
  });

  if (!active) return null;

  const pileBaseR = 0.025 * Math.sqrt(saltAmount);
  const pileHeight = 0.02 * saltAmount;

  return (
    <>
      <group position={[px, py, pz]} rotation={[0, 0, rotZ]}>
        <mesh position={[-0.085, 0, 0]} castShadow>
          <boxGeometry args={[0.05, 0.008, 0.04]} />
          <meshStandardMaterial color="#c0c8d4" metalness={0.55} roughness={0.4} />
        </mesh>
        <mesh position={[0.04, 0, 0]} castShadow>
          <boxGeometry args={[0.18, 0.012, 0.012]} />
          <meshStandardMaterial color="#9ba4b1" metalness={0.6} roughness={0.45} />
        </mesh>
        {saltAmount > 0.04 && (
          <mesh position={[-0.085, 0.004 + pileHeight / 2, 0]} castShadow>
            <coneGeometry args={[pileBaseR, pileHeight, 16]} />
            <meshStandardMaterial color="#f4f4f4" roughness={0.95} />
          </mesh>
        )}
      </group>

      <instancedMesh ref={fallRef} args={[undefined, undefined, NAPH_FALL_PARTICLES]} castShadow>
        <icosahedronGeometry args={[1, 0]} />
        <meshStandardMaterial color="#f4f4f4" roughness={0.95} />
      </instancedMesh>
    </>
  );
}

interface ThermometerInsertionProps {
  active: boolean;
  progress: number;
  temperature: number;
  isStirring: boolean;
}

export function ThermometerInsertion({ active, progress, temperature, isStirring }: ThermometerInsertionProps) {
  if (!active) return null;
  const START_Y = 2.6;
  const END_Y = 1.5;
  const y = lerp(START_Y, END_Y, easeInOut(progress));
  return (
    <Thermometer position={[0, y, 0]} temperature={temperature} isStirring={isStirring} />
  );
}

interface BoilingTubeImmersionProps {
  active: boolean;
  progress: number;
  substanceLevel: number;
  stateOfMatter: 'Solid' | 'Solid+Liquid' | 'Liquid';
}

export function BoilingTubeImmersion({ active, progress, substanceLevel, stateOfMatter }: BoilingTubeImmersionProps) {
  if (!active) return null;
  // Brief lift-then-lower confirmation gesture (the tube was already nominally
  // hovering at the final position; this dip signals "now immersed in bath").
  const START_Y = 1.7;
  const END_Y = TUBE_FINAL_Y;
  const y = lerp(START_Y, END_Y, easeInOut(progress));
  return (
    <BoilingTube position={[0, y, 0]} scale={1} substanceLevel={substanceLevel} stateOfMatter={stateOfMatter} />
  );
}

interface BoilingTubeTransferProps {
  active: boolean;
  progress: number;
}

const FLASK_POS: [number, number, number] = [0.7, 0, 0];
const FLASK_SCALE = 0.3;

export function BoilingTubeTransfer({ active, progress }: BoilingTubeTransferProps) {
  if (!active) return null;

  const liftEnd = 0.3;
  const tiltEnd = 0.7;

  const lift = progress < liftEnd ? easeInOut(progress / liftEnd) : 1;
  const tiltT = progress < liftEnd
    ? 0
    : progress < tiltEnd
      ? easeInOut((progress - liftEnd) / (tiltEnd - liftEnd))
      : 1;
  const pourT = progress < tiltEnd ? 0 : Math.max(0, Math.min(1, (progress - tiltEnd) / (1 - tiltEnd)));

  const tubeX = lerp(0, 0.55, tiltT);
  const tubeYBeforeTilt = lerp(TUBE_FINAL_Y, 2.0, lift);
  const tubeYAfterTilt = lerp(2.0, 1.65, tiltT);
  const tubeYFinal = progress < liftEnd ? tubeYBeforeTilt : tubeYAfterTilt;
  const tubeRotZ = lerp(0, -1.1, tiltT);

  const tubeSubstance = 0.4 * (1 - pourT);
  const flaskFill = 0.55 * pourT;

  return (
    <>
      <group position={[tubeX, tubeYFinal, 0]} rotation={[0, 0, tubeRotZ]}>
        <BoilingTube
          position={[0, 0, 0]}
          scale={1}
          substanceLevel={tubeSubstance}
          stateOfMatter="Liquid"
        />
      </group>

      <ConicalFlask
        position={FLASK_POS}
        scale={FLASK_SCALE}
        showLiquid={flaskFill > 0.01}
        liquidColor="#f0f8ff"
        liquidLabel=""
        liquidFillRatio={flaskFill}
        isReacting={false}
        isPaused={true}
      />
    </>
  );
}
